<template>
  <v-dialog
    :model-value="modelValue"
    max-width="560"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card class="pa-2">
      <v-card-title class="text-h6">
        {{ $t("acceptance.corrections.dialog_title", { group: groupLabel }) }}
      </v-card-title>
      <v-card-text>
        <div class="text-body-2 text-grey-darken-1 mb-3">
          {{
            $t(
              fieldset
                ? fieldset.introKey || "acceptance.pra.corrections.intro"
                : "acceptance.corrections.intro",
            )
          }}
        </div>

        <v-alert
          v-if="error"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ error }}
        </v-alert>

        <div v-for="path in fields" :key="path">
          <v-text-field
            v-if="spec(path).type === 'date'"
            type="date"
            variant="outlined"
            density="comfortable"
            class="mb-1"
            :label="$t(spec(path).label)"
            :model-value="form[path]"
            :min="limitOf(path).min || undefined"
            :max="limitOf(path).max || maxDate || undefined"
            :hint="dateHint(path)"
            persistent-hint
            @update:model-value="setField(path, $event)"
          />
          <!-- Workshop (Stage 3a): a performed-at date and time, a decimal quantity. -->
          <v-text-field
            v-else-if="spec(path).type === 'datetime'"
            type="datetime-local"
            variant="outlined"
            density="comfortable"
            class="mb-1"
            :label="$t(spec(path).label)"
            :model-value="form[path]"
            :min="fieldLimit(path).min || undefined"
            :max="fieldLimit(path).max || undefined"
            :hint="currentHint(path)"
            persistent-hint
            @update:model-value="setField(path, $event)"
          />
          <v-text-field
            v-else-if="spec(path).type === 'number'"
            inputmode="decimal"
            variant="outlined"
            density="comfortable"
            class="mb-1"
            :label="$t(spec(path).label)"
            :model-value="form[path]"
            :hint="currentHint(path)"
            persistent-hint
            @update:model-value="setField(path, $event)"
          />
          <!-- Stage 6, waste on: a workshop container is picked, not typed. -->
          <container-picker
            v-else-if="spec(path).type === 'container'"
            class="mb-1"
            :model-value="form[path]"
            :task-code="spec(path).taskCode"
            :unit="spec(path).unit"
            :required="spec(path).required"
            :label="spec(path).label"
            :hint="currentHint(path)"
            @update:model-value="setField(path, $event || null)"
          />
          <v-text-field
            v-else-if="spec(path).type === 'text' && visible(path)"
            variant="outlined"
            density="comfortable"
            class="mb-1"
            :label="$t(spec(path).label)"
            :model-value="form[path]"
            :maxlength="spec(path).max"
            :hint="currentHint(path)"
            persistent-hint
            @update:model-value="setField(path, $event)"
          />
          <v-select
            v-else-if="spec(path).type === 'select'"
            variant="outlined"
            density="comfortable"
            class="mb-1"
            :clearable="!isAnswer(path) && !fieldset"
            :label="$t(spec(path).label)"
            :items="optionItems(path)"
            :model-value="form[path]"
            :hint="currentHint(path)"
            persistent-hint
            @update:model-value="setField(path, $event || null)"
          />
          <v-autocomplete
            v-else-if="spec(path).type === 'country'"
            variant="outlined"
            density="comfortable"
            class="mb-1"
            clearable
            :label="$t(spec(path).label)"
            :items="countryItems"
            :model-value="form[path]"
            :hint="currentHint(path)"
            persistent-hint
            @update:model-value="setField(path, $event || null)"
          />
        </div>

        <!-- Arrival date: the recalculated deadlines come from a dry run. -->
        <template v-if="isArrival && changed.length">
          <div v-if="!dateOk" class="text-caption text-red-darken-2 mb-2">
            {{ $t("acceptance.corrections.date_invalid") }}
          </div>
          <template v-else>
            <v-alert
              v-if="movedLater"
              type="warning"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              {{ $t("acceptance.corrections.moved_later") }}
            </v-alert>
            <v-progress-linear
              v-if="preview && preview.loading"
              indeterminate
              class="mb-3"
            />
            <div
              v-else-if="preview && preview.error"
              class="text-caption text-red-darken-2 mb-3"
            >
              {{ preview.error }}
            </div>
            <v-table v-else-if="rows.length" density="compact" class="mb-3">
              <thead>
                <tr>
                  <th class="text-left">
                    {{ $t("acceptance.corrections.preview_deadline") }}
                  </th>
                  <th class="text-left">
                    {{ $t("acceptance.corrections.preview_current") }}
                  </th>
                  <th class="text-left">
                    {{ $t("acceptance.corrections.preview_after") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in rows" :key="row.kind">
                  <td>{{ $t(DEADLINE_LABELS[row.kind]) }}</td>
                  <td>{{ row.from || "—" }}</td>
                  <td class="font-weight-bold">{{ row.to }}</td>
                </tr>
              </tbody>
            </v-table>
          </template>
        </template>

        <!-- PRA case (Stage 2): every change is previewed with a dry run, which lists the changes
             and warns when a request date becomes late. -->
        <template v-if="fieldset && changed.length">
          <div v-if="inputMessage" class="text-caption text-red-darken-2 mb-2">
            {{ inputMessage }}
          </div>
          <template v-else-if="!inputProblem">
            <v-progress-linear
              v-if="preview && preview.loading"
              indeterminate
              class="mb-3"
            />
            <div
              v-else-if="preview && preview.error"
              class="text-caption text-red-darken-2 mb-3"
            >
              {{ preview.error }}
            </div>
            <template v-else-if="preview && preview.data">
              <v-alert
                v-if="lateWarning"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                {{
                  $t("acceptance.pra.corrections.late_warning", {
                    date: lateWarning.dueDate,
                  })
                }}
              </v-alert>
              <v-alert
                v-for="key in fieldsetWarnings"
                :key="key"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-3"
              >
                {{ $t(key) }}
              </v-alert>
              <v-table v-if="changeRows.length" density="compact" class="mb-3">
                <thead>
                  <tr>
                    <th class="text-left">
                      {{ $t("acceptance.pra.corrections.preview_field") }}
                    </th>
                    <th class="text-left">
                      {{ $t("acceptance.corrections.preview_current") }}
                    </th>
                    <th class="text-left">
                      {{ $t("acceptance.corrections.preview_after") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in changeRows" :key="row.field">
                    <td>{{ $t(row.label) }}</td>
                    <td>{{ shown(row.from) }}</td>
                    <td class="font-weight-bold">{{ shown(row.to) }}</td>
                  </tr>
                </tbody>
              </v-table>
            </template>
          </template>
        </template>

        <v-text-field
          variant="outlined"
          density="comfortable"
          :label="`${$t('acceptance.corrections.reason')} *`"
          :hint="$t('acceptance.corrections.reason_hint')"
          persistent-hint
          maxlength="500"
          :model-value="reason"
          @update:model-value="reason = $event || ''"
        />
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          variant="outlined"
          color="primary"
          :disabled="saving"
          @click="$emit('update:modelValue', false)"
        >
          {{ $t("acceptance.corrections.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          color="primary"
          :loading="saving"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ $t("acceptance.corrections.submit") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import COUNTRY_CODES from "@/components/Acceptance/countries";
import ContainerPicker from "@/components/Waste/containerPicker.vue";
import {
  ARRIVAL_DATE_GROUP,
  buildCountryItems,
  changedFields,
  correctionValue,
  fieldSpec,
  groupFields,
  isLater,
  previewRows,
  previewableDate,
  valueDisplay,
} from "@/components/Acceptance/corrections.mjs";

// group -> acceptance.corrections.groups.<key> (locale keys have no dots)
const GROUP_KEYS = {
  "arrival.date": "arrival_date",
  "identity.vin": "identity_vin",
  "identity.plate": "identity_plate",
  "identity.vehicleClass": "identity_vehicle_class",
  owner: "owner",
  deliverer: "deliverer",
  authorityToDeliver: "authority",
};

const DEADLINE_LABELS = {
  depollution: "acceptance.deadlines.depollution",
  deregistrationRequest: "acceptance.deadlines.deregistration",
};

const PREVIEW_DELAY_MS = 400;

// The Stage 1 field helpers; a PRA case passes its own (praCorrections.mjs PRA_FIELDSET).
const STAGE1 = {
  groupFields,
  fieldSpec,
  valueDisplay,
  changedFields,
  correctionValue,
};

// One correction of a frozen field group (Manager / Admin). The page runs the requests: this
// dialog emits `submit` and, for the arrival date, `preview` (a dry run, debounced). With
// `fieldset` (Stage 2, a PRA case) every change is previewed: `preview` carries { group, value }.
export default {
  name: "AcceptanceCorrectionDialog",
  components: { ContainerPicker },
  props: {
    modelValue: { type: Boolean, default: false },
    // Correction group being edited ("owner", "arrival.date", "checklist.plates", ...)
    group: { type: String, default: null },
    // The saved form (flat, keyed by API path): the current values.
    values: { type: Object, default: () => ({}) },
    // dossier.checklist, for the line's name and whether it may be "not applicable"
    lines: { type: Array, default: () => [] },
    // Latest allowed arrival date: the Europe/Rome day of the confirmation
    maxDate: { type: String, default: null },
    saving: { type: Boolean, default: false },
    error: { type: String, default: "" },
    // Dry run of the arrival date: { loading, error, data }
    preview: { type: Object, default: null },
    // Stage 2: the PRA helpers (PRA_FIELDSET) instead of the Stage 1 ones, and the live date
    // limits by path ({ min, max }, praCorrections.praDateLimits). Stage 3a: a workshop result's
    // helpers (workshopCorrections.workshopFieldset), which add introKey, problemKey,
    // dateLimits and warnings.
    fieldset: { type: Object, default: null },
    limits: { type: Object, default: null },
  },
  emits: ["update:modelValue", "submit", "preview"],
  data: () => ({
    form: {},
    reason: "",
    DEADLINE_LABELS,
  }),
  computed: {
    helpers() {
      return this.fieldset || STAGE1;
    },
    fields() {
      return this.helpers.groupFields(this.group);
    },
    changed() {
      return this.helpers.changedFields(this.group, this.form, this.values);
    },
    // PRA: what stops the preview (null when it can run).
    inputProblem() {
      if (!this.fieldset) return null;
      return this.fieldset.inputProblem(
        this.group,
        this.form,
        this.limits || {},
      );
    },
    inputMessage() {
      // Workshop: the fieldset names the message of each problem.
      if (this.fieldset && this.fieldset.problemKey) {
        const key = this.fieldset.problemKey(this.inputProblem);
        return key ? this.$t(key) : "";
      }
      const path = this.fields[0];
      if (this.inputProblem === "date_range") {
        const { min, max } = this.limitOf(path);
        return this.$t("acceptance.pra.corrections.date_invalid", {
          min: this.civil(min) || "—",
          max: this.civil(max),
        });
      }
      if (this.inputProblem === "too_long") {
        const spec = this.fields
          .map((item) => this.spec(item))
          .find((item) => item.max);
        return this.$t("acceptance.pra.crd.dialog.errors.too_long", {
          max: spec ? spec.max : "",
        });
      }
      if (this.inputProblem === "agency_required") {
        return this.$t("acceptance.pra.request.errors.agency_required");
      }
      return "";
    },
    changeRows() {
      if (!this.fieldset || !this.preview) return [];
      return this.fieldset.previewRows(this.preview.data);
    },
    lateWarning() {
      if (!this.fieldset || !this.preview) return null;
      return this.fieldset.lateWarning(this.preview.data);
    },
    // Workshop: the dry run's warnings as i18n keys.
    fieldsetWarnings() {
      if (!this.fieldset || !this.fieldset.warnings || !this.preview) return [];
      return this.fieldset.warnings(this.preview.data);
    },
    isArrival() {
      return this.group === ARRIVAL_DATE_GROUP;
    },
    dateOk() {
      return previewableDate(this.form["arrival.date"], this.maxDate);
    },
    movedLater() {
      return isLater(this.values["arrival.date"], this.form["arrival.date"]);
    },
    rows() {
      return previewRows(this.preview && this.preview.data);
    },
    checklistLine() {
      const line = /^checklist\.(.+)$/.exec(this.group || "");
      return line ? this.lines.find((item) => item.code === line[1]) : null;
    },
    groupLabel() {
      if (this.checklistLine) {
        const key = `acceptance.checklist.lines.${this.checklistLine.code}`;
        const name =
          this.$t(key) === key ? this.checklistLine.label : this.$t(key);
        return this.$t("acceptance.corrections.groups.checklist", {
          line: name,
        });
      }
      if (this.fieldset) {
        const labelKey = this.fieldset.groupLabelKey(this.group);
        return labelKey ? this.$t(labelKey) : "";
      }
      const key = GROUP_KEYS[this.group];
      return key ? this.$t(`acceptance.corrections.groups.${key}`) : "";
    },
    countryItems() {
      return buildCountryItems(COUNTRY_CODES, this.$i18n.locale);
    },
    answer() {
      return this.checklistLine
        ? this.form[`checklist.${this.checklistLine.code}.answer`]
        : null;
    },
    // A checklist line needs a reason for an exception or "not applicable".
    reasonMissing() {
      if (!this.checklistLine) return false;
      const needs =
        this.answer === "exception" || this.answer === "not_applicable";
      const reason = this.form[`checklist.${this.checklistLine.code}.reason`];
      return needs && !(reason && String(reason).trim());
    },
    canSubmit() {
      if (this.saving || !this.changed.length || !this.reason.trim())
        return false;
      if (this.checklistLine && (!this.answer || this.reasonMissing))
        return false;
      if (this.fieldset) {
        return Boolean(
          !this.inputProblem &&
            this.preview &&
            !this.preview.loading &&
            !this.preview.error &&
            this.preview.data,
        );
      }
      if (this.isArrival) {
        return Boolean(
          this.dateOk &&
            this.preview &&
            !this.preview.loading &&
            !this.preview.error &&
            this.preview.data,
        );
      }
      return true;
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.reset();
      else this.clearTimer();
    },
    form(form) {
      if (!this.modelValue) return;
      if (this.fieldset) this.scheduleFieldsetPreview();
      else if (this.isArrival) this.schedulePreview(form["arrival.date"]);
    },
  },
  beforeUnmount() {
    this.clearTimer();
  },
  methods: {
    spec(path) {
      return this.helpers.fieldSpec(path);
    },
    // { min, max } of a PRA date (the neighbouring recorded dates and today), else {}.
    limitOf(path) {
      return (this.limits && this.limits[path]) || {};
    },
    civil(date) {
      return date ? moment(date, "YYYY-MM-DD").format("DD/MM/YYYY") : "";
    },
    // A workshop display may have segments, joined with " · " (a quantity "41 kg · Stimata").
    shown(display) {
      if (!display || display.empty) return "—";
      if (display.segments) {
        return display.segments.map((part) => this.shown(part)).join(" · ");
      }
      return display.key ? this.$t(display.key) : display.text;
    },
    // Workshop: the performed-at limits ({ min, max }) from the fieldset.
    fieldLimit(path) {
      return (
        (this.fieldset && this.fieldset.dateLimits
          ? this.fieldset.dateLimits(path)
          : null) || {}
      );
    },
    isAnswer(path) {
      return /^checklist\..+\.answer$/.test(path);
    },
    // The reason of a checklist line only shows for an answer that needs one.
    visible(path) {
      if (this.fieldset) return this.fieldset.visible(path, this.form);
      if (!/^checklist\..+\.reason$/.test(path)) return true;
      return this.answer === "exception" || this.answer === "not_applicable";
    },
    reset() {
      const form = {};
      this.helpers.groupFields(this.group).forEach((path) => {
        form[path] = this.values[path] === undefined ? null : this.values[path];
      });
      this.form = form;
      this.reason = "";
      this.clearTimer();
    },
    setField(path, value) {
      const next = { ...this.form, [path]: value };
      if (this.isAnswer(path) && value === "present") {
        next[path.replace(/\.answer$/, ".reason")] = null;
      }
      this.form = next;
    },
    optionItems(path) {
      const { options, optionKey } = this.spec(path);
      return (options || [])
        .filter(
          (value) =>
            !(
              value === "not_applicable" &&
              this.checklistLine &&
              !this.checklistLine.allowNotApplicable
            ),
        )
        .map((value) => ({
          value,
          title: optionKey ? this.$t(`${optionKey}.${value}`) : value,
        }));
    },
    // "Attuale: …"
    currentHint(path) {
      const display = this.helpers.valueDisplay(path, this.values[path]);
      return this.$t("acceptance.corrections.current", {
        value: this.shown(display),
      });
    },
    dateHint(path) {
      if (this.fieldset) {
        const { min, max } = this.limitOf(path);
        const range = min
          ? this.$t("acceptance.pra.corrections.date_range", {
              min: this.civil(min),
              max: this.civil(max),
            })
          : this.$t("acceptance.pra.corrections.date_until", {
              max: this.civil(max),
            });
        return `${this.currentHint(path)} · ${range}`;
      }
      const max = this.maxDate
        ? moment(this.maxDate, "YYYY-MM-DD").format("DD/MM/YYYY")
        : "";
      return `${this.currentHint(path)} · ${this.$t("acceptance.corrections.date_max", { date: max })}`;
    },
    // The dry run starts when the typing pauses on a valid, changed date; anything else clears it.
    schedulePreview(date) {
      this.clearTimer();
      const changed = date !== this.values["arrival.date"];
      if (!changed || !previewableDate(date, this.maxDate)) {
        this.$emit("preview", null);
        return;
      }
      this.timer = setTimeout(() => {
        this.timer = null;
        this.$emit("preview", { date });
      }, PREVIEW_DELAY_MS);
    },
    // PRA: the dry run starts when the typing pauses on a valid change; anything else clears it.
    scheduleFieldsetPreview() {
      this.clearTimer();
      if (!this.changed.length || this.inputProblem) {
        this.$emit("preview", null);
        return;
      }
      const request = {
        group: this.group,
        value: this.helpers.correctionValue(this.group, this.form),
      };
      this.timer = setTimeout(() => {
        this.timer = null;
        this.$emit("preview", request);
      }, PREVIEW_DELAY_MS);
    },
    clearTimer() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
    },
    submit() {
      if (!this.canSubmit) return;
      this.$emit("submit", {
        group: this.group,
        value: this.helpers.correctionValue(this.group, this.form),
        reason: this.reason.trim(),
      });
    },
  },
};
</script>
